import { create } from 'zustand';
import type { GcsConfig } from '../types/gcs/gcsConfig';
import { GCS_CONFIGS } from '../config/gcs/gcsConfigs';
import { localStorageService } from '../services/storage/LocalStorage';
import { createLogger } from '../utils/logger';
import { useGcsStore } from './useGcsStore';

interface SelectedGcsConfigsStore {
  selectedConfigs: GcsConfig[];
  addGcsConfig: (id: string, connect: boolean) => void;
  removeGcsConfig: (id: string) => void;
  setSelectedGcsConfigs: (ids: Set<string>) => void;
}

const logger = createLogger('SelectedGcsConfigsStore');

// Load initial state from localStorage
const loadInitialConfigs = (): GcsConfig[] => {
  const selectedIds = localStorageService.getSelectedGcsIds();
  if (selectedIds) {
    logger.info('Loaded selected GCS config IDs', selectedIds);
    return GCS_CONFIGS.filter((config) => selectedIds.includes(config.id));
  }
  logger.info('No selected GCS config IDs found');
  return [];
};

export const useSelectedGcsConfigsStore = create<SelectedGcsConfigsStore>((set) => {
  const initialConfigs = loadInitialConfigs();
  const gcsStore = useGcsStore.getState();

  initialConfigs.forEach((config) => {
    gcsStore.addGcsConnection(config);
  });

  return {
    selectedConfigs: initialConfigs,

    addGcsConfig: (id: string, connect: boolean) => {
      logger.info('Adding GCS config', id);
      const selectedIds = localStorageService.getSelectedGcsIds() || [];
      const config = GCS_CONFIGS.find((configItem) => configItem.id === id);
      if (!config) {
        logger.warn('Config not found for id', id);
        return;
      }
      if (selectedIds.includes(id)) {
        logger.info('Config already selected', id);
        return;
      }
      const updatedIds = [...selectedIds, id];
      localStorageService.setSelectedGcsIds(updatedIds);
      const configs = GCS_CONFIGS.filter((configItem) => updatedIds.includes(configItem.id));
      set({ selectedConfigs: configs });

      gcsStore.addGcsConnection(config);
      if (connect) {
        gcsStore.connect(id);
      }
    },

    removeGcsConfig: (id: string) => {
      logger.info('Removing GCS config', id);
      const selectedIds = localStorageService.getSelectedGcsIds() || [];
      const updatedIds = selectedIds.filter((configId) => configId !== id);
      localStorageService.setSelectedGcsIds(updatedIds);
      const configs = GCS_CONFIGS.filter((config) => updatedIds.includes(config.id));
      set({ selectedConfigs: configs });

      gcsStore.disconnect(id);
      gcsStore.removeGcsConnection(id);
    },

    setSelectedGcsConfigs: (ids: Set<string>) => {
      logger.info('Setting selected GCS configs', Array.from(ids));
      const currentIds = new Set(localStorageService.getSelectedGcsIds() || []);
      const store = useSelectedGcsConfigsStore.getState();

      ids.forEach((id) => {
        if (!currentIds.has(id)) {
          store.addGcsConfig(id, false);
        }
      });

      currentIds.forEach((id) => {
        if (!ids.has(id)) {
          store.removeGcsConfig(id);
        }
      });
    }
  };
});
